import React from 'react';
import cx from 'classnames';

import {Autocomplete, AutocompleteItem, Input, Select, SelectSection, SelectItem, SelectProps, Skeleton} from "@heroui/react";

import FlexRow from './FlexRow';

export type DropdownProps = SelectProps & {
  label?:			string;
  flex?:			number;
  isLoaded?:		boolean;
  minWidth?:		any;
  maxSelected?:		number;
  width?:			any;
  height?:			any;
  options:			FieldInfo[];
  value?:			React.Key | React.Key[];
  setValue?:		(val: any) => any;
  row?:				boolean;
  // Multi-select only: puts a search box at the top of the open list and
  // filters the options as you type. The single-select variant is an
  // Autocomplete, which searches already.
  isSearchable?:	boolean;
  searchPlaceholder?:	string;
};

export interface FieldInfo {
	value:			any;
	desc:			string;
	data?:			any;
	// Hover text for one option. Used with `disabledKeys` to say WHY an option
	// cannot be picked (e.g. "MW light user on this project") instead of
	// greying it out with no explanation.
	tooltip?:		string;
}

const dropdownDefaultH = "h-auto"; //36;

const styles = {
  flex: [
    'flex-0',
    'flex-1',
    'flex-2',
    'flex-3',
  ],
};

const pick = (...props: any[]) => (o: any) => props.reduce((a, e) => ({ ...a, [e]: o[e] }), {});

// The search box of a searchable multi-select. It lives in the listbox's
// topContent, so it mounts when the list opens and is gone when it closes.
const DropdownSearch = (sprops: {
  value:			string;
  setValue:			(val: string) => any;
  placeholder?:		string;
  inputRef:			React.MutableRefObject<HTMLInputElement | null>;
}) => {
  const inputRef = sprops.inputRef;

  // HeroUI hands the open list's focus to the listbox itself in a layout
  // effect, so the caret is put here one frame later - otherwise the first
  // letters typed would run the listbox's type-ahead instead of landing in
  // the box.
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (inputRef.current)
        inputRef.current.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      inputRef.current = null;
    };
  }, []);

  // Same type-ahead the other way round: a keystroke that reaches the listbox
  // moves its highlight, so the letters stop here. Escape and Tab still
  // bubble, so the list can be closed and left by keyboard.
  const handleKeyDown = (evt: React.KeyboardEvent) => {
    if (	evt.key !== "Escape" &&
    		evt.key !== "Tab"
       )
      evt.stopPropagation();
  };

  return (
    <div className="sticky top-0 z-20 bg-white px-[8px] pt-[8px] pb-[4px]"
         onKeyDown={handleKeyDown}>
      <Input ref={inputRef}
             size="sm"
             isClearable
             placeholder={sprops.placeholder}
             value={sprops.value}
             onValueChange={sprops.setValue}
             aria-label={sprops.placeholder ? sprops.placeholder : "search"}
             startContent={
               <span className="material-icons text-gray-400 text-sm">search</span>
             } />
    </div>
  );
};

const Dropdown = (dprops: DropdownProps) => {
  const {className, children, disabledKeys, isDisabled, label, height, flex, isLoaded, maxSelected, options, selectionMode, value, width, minWidth, row, setValue, onChange, variant, isSearchable, searchPlaceholder, isVirtualized, listboxProps, onOpenChange, ...props} = dprops;

  const autoProps = pick('isInvalid', 'errorMessage', 'isDisabled')(dprops);

  const [search, setSearch] = React.useState('');
  const searchRef = React.useRef(null as HTMLInputElement | null);
  
  const mw =	minWidth === undefined
	  			?	"min-w-[250px]"
	  			:	minWidth;
  
  const ms =	maxSelected === undefined
  				?	1000
  				:	maxSelected;
  
  const h = height
  			?	height
  			:	dropdownDefaultH;
  
  const f = flex === undefined
  			?	1
  			:	flex;
  
  const v = variant === undefined
  			?	"bordered"
  			:	variant;
  
  const sm = selectionMode=== undefined
  			 ?	"single"
  			 :	selectionMode;
  
  const loaded = isLoaded === undefined
  				 ?	true
  				 :	isLoaded;
  
  const sendVal =	sm === "multiple"
  					?	value
  					:	(	Array.isArray(value) &&
  							value.length
  							?	value[0]
  							:	""
  						);
  
  const classes = cx(
    'flex',
    styles.flex[f],
    width === undefined ? 'w-full' : width,
    h,
    className,
  );
  
  /*const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (setValue)
      setValue(
      	event.target && event.target.value
        ?	event.target.value
        :	''
      );
  };*/
  
  const handleSelectionChange = (vals: "all" | Set<React.Key>) => {
    if (	vals === "all" ||
        	!setValue
       )
      return
      
    setValue(
        selectionMode == "multiple"
        ?	[...vals]
        :	(	vals.size
        		?	(	[...vals][0]
        				?	[...vals][0]
        				:	""
        			)
        		:	""
        	)
    );

    // Pressing an option moves the focus onto it, which would send the next
    // letters typed to the listbox's own type-ahead while the search box
    // still shows the old text - as if typing had stopped working. Picking a
    // name hands the box back, so the next one can be searched for straight
    // away.
    const input = searchRef.current;
    if (input) {
      requestAnimationFrame(() => {
        if (document.contains(input))
          input.focus();
      });
    }
  };
  
  const handleAutoSelectionChange = (val: React.Key | null) => {
    if (!setValue)
      return;
    
    setValue(
    	val
    	?	(	Array.isArray(value)
    			?	[val]
    			:	val
    		)
    	:	(	Array.isArray(value)
    			?	[]
    			:	''
    		)
    );
  };
  
  const selKeys: any[] =	value
  							?	(	Array.isArray(value)
  									?	value
  									:	[value]
  								)
  							:	[];

  // Only the multi-select needs one: the single-select below is already an
  // Autocomplete, which has its own.
  const searchable =	isSearchable === true &&
  						sm === "multiple";

  const needle =	searchable
  					?	search.trim().toLowerCase()
  					:	'';

  // Matched on the text an option shows AND on the value behind it, so a
  // username finds its entry whatever its label spells out.
  const optMatches = (opt: FieldInfo) =>
    String(opt.desc === undefined ? '' : opt.desc).toLowerCase().indexOf(needle) > -1 ||
    String(opt.value === undefined ? '' : opt.value).toLowerCase().indexOf(needle) > -1;

  // An option that is already selected stays in the list even when it does
  // not match: HeroUI reads the names shown in the closed field out of the
  // options it was given, so dropping one mid-search would blank it in the
  // field and warn about a selected key it cannot find.
  const shownOptions =	needle
  						?	(options || []).filter(
  								(opt: FieldInfo) =>
  									optMatches(opt) ||
  									selKeys.indexOf(opt.value) > -1
  							)
  						:	options;

  const handleOpenChange = (open: boolean) => {
    // Next opening starts on the full list rather than on whatever was typed
    // the last time.
    if (!open)
      setSearch('');

    if (onOpenChange)
      onOpenChange(open);
  };

  const searchListboxProps =	searchable
  							?	{
  									...listboxProps,
  									topContent:	<DropdownSearch value={search}
  									                            setValue={setSearch}
  									                            placeholder={searchPlaceholder}
  									                            inputRef={searchRef} />,
  								}
  							:	listboxProps;

  const out = 
    sm == "multiple"
    ?  
  <Select className={classes}
          classNames={{
            trigger: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] max-h-[52px]",
            label: "!text-black r10",
            listboxWrapper: "px-0",
            listbox: "px-0",
            popoverContent: "px-0",
            value: "!text-black r14",
          }}
          selectedKeys={selKeys}
          variant={v}
		  label={label}
          selectionMode={sm}
          isDisabled={isDisabled}
          // Passed through rather than swallowed: callers use it to stop a
          // name being picked in two mutually exclusive lists at once.
          disabledKeys={disabledKeys}
          disallowEmptySelection={true}
          //onChange={handleChange}
          onSelectionChange={handleSelectionChange}
          onOpenChange={handleOpenChange}
          listboxProps={searchListboxProps}
          // Searching changes how many options are listed, and HeroUI swaps
          // in a virtualised list on its own above 50 of them - that swap
          // remounts the search box mid-word and drops the caret, so a
          // searchable list stays unvirtualised.
          isVirtualized={searchable ? false : isVirtualized}
          value={value}
          aria-label="select"
          {...props}>
    {shownOptions?.map((opt: FieldInfo, i: number) =>
      <SelectItem key={opt.value}
                  classNames={{
                    base: "px-[8px] rounded-none data-[hover=true]:bg-[--primetime-30] data-[selectable=true]:focus:bg-[--primetime-30]",
                  }}
                  textValue={opt.desc}
                  aria-label={opt.desc}>
        {opt.tooltip
          ? <span title={opt.tooltip}>{opt.desc}</span>
          : opt.desc
        }
      </SelectItem>
    )}
  </Select>
    :
  <Autocomplete className={classes}
          classNames={{
            //trigger: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] max-h-[52px]",
            //label: "!text-black r10",
            listboxWrapper: "px-0",
            listbox: "px-0",
            popoverContent: "px-0",
            //value: "!text-black r14",
          }}
          defaultItems={options}
          selectedKey={selKeys.length ? selKeys[0] : ''}
          variant={v}
		  label={label}
          onSelectionChange={handleAutoSelectionChange}
          aria-label="select"
          value={sendVal}
          {...autoProps}>
    {(item: FieldInfo) =>
      <AutocompleteItem key={item.value}>
        {item.desc}
      </AutocompleteItem>
    }
  </Autocomplete>;
  
  if (!loaded) {
    return (
      <Skeleton isLoaded={loaded}
                className="w-full">
        { out }
      </Skeleton>
    );
  }
  
  return out;
}

export default Dropdown;


